import { z } from "zod";
import type { Locale } from "./common";
import {
  CARE_HISTORY_LABEL,
  CARE_HISTORY_LABEL_BY_LOCALE,
  careHistoryTargetSchema,
} from "./din-vard";
import { MEETING_NOTICE_COPY } from "./meeting-notice";
import { MEETING_SUMMARY_FIELD_LABEL } from "./meeting-summary";
import { patientMedicalNoticeSchema } from "./medical-question";
import { onboardingGuideSchema } from "./onboarding-guide";

// TES-1466 (TES-1458 §2, Emelie 2026-10-20) — "Meddelanden → Läkare": one
// chronological list of what the doctor has published to her, read from the
// rows Din vård already reads. Nothing here is a copy:
//   "Läkare visar besked i kronologisk ordning. Beslut, behandlingsändringar och provsvar visar förhandsvisning med länk till originalet under Din vård. Fristående publicerade läkarmeddelanden och avgränsade läkarfrågor kan visas i sin helhet i kanalen."
//   "Samma original, version och lässtatus används mellan ingångarna. Inga dubbla meddelanden eller notifieringar."
//
// Three display modes:
//   full    — the notice itself (its id, version, text, the doctor's question
//             with her answer, a meeting message's CTA): doctor_notice,
//             medical_question_answer, meeting_info, meeting_proposal.
//   preview — type, sender and date only, with the SAME target Din vård's
//             history opens (careHistoryTargetSchema): a decision, a monthly
//             review, a changed plan or a renewal opens that version of Din
//             behandling, the doctor's word on a lab result opens the
//             sampling under "Prover och mätningar", the meeting summary
//             opens the note. No text: "Ingen full dosordination lösryckt i
//             kanalen."
//   system  — a system-marked status post, never worded as the doctor's own
//             message: the activated treatment, and a prescription ready for
//             pickup ("Aktiverad behandling/tillgänglig uthämtning visas som
//             tydligt systemmärkt statuspost i samma kronologi").
//
// Coach and Ella Support messages are never read here. The route sends no
// push and writes nothing: the items already had their one notification
// when they were published.
//
// Served only to a build at or after CLIENT_CONTRACT_CHANGES.messagesHub;
// every other build is sent an empty list (no build before it calls the
// route).

export const DOCTOR_FEED_ITEM_TYPES = [
  // full
  "doctor_notice",
  "medical_question_answer",
  "meeting_info",
  "meeting_proposal",
  // preview
  "decision",
  "monthly_review",
  "plan_changed",
  "renewal",
  "lab_feedback",
  "meeting_summary",
  // system
  "activation",
  "prescription_ready",
] as const;
export const doctorFeedItemTypeSchema = z.enum(DOCTOR_FEED_ITEM_TYPES);
export type DoctorFeedItemType = z.infer<typeof doctorFeedItemTypeSchema>;

export const DOCTOR_FEED_DISPLAY_MODES = ["full", "preview", "system"] as const;
export const doctorFeedDisplayModeSchema = z.enum(DOCTOR_FEED_DISPLAY_MODES);
export type DoctorFeedDisplayMode = z.infer<typeof doctorFeedDisplayModeSchema>;

export const DOCTOR_FEED_DISPLAY_MODE: Record<
  DoctorFeedItemType,
  DoctorFeedDisplayMode
> = {
  doctor_notice: "full",
  medical_question_answer: "full",
  meeting_info: "full",
  meeting_proposal: "full",
  decision: "preview",
  monthly_review: "preview",
  plan_changed: "preview",
  renewal: "preview",
  lab_feedback: "preview",
  meeting_summary: "preview",
  activation: "system",
  prescription_ready: "system",
};

// The existing titles, nothing new: 09C's type labels for the full items,
// Din vård's history labels for the previews, A-02's heading for the meeting
// summary.
export const DOCTOR_FEED_LABEL: Record<
  Exclude<DoctorFeedItemType, "activation" | "prescription_ready">,
  string
> = {
  doctor_notice: MEETING_NOTICE_COPY.typeLabel.doctor_notice,
  medical_question_answer: MEETING_NOTICE_COPY.typeLabel.doctor_notice,
  meeting_info: MEETING_NOTICE_COPY.typeLabel.meeting_info,
  meeting_proposal: MEETING_NOTICE_COPY.typeLabel.meeting_proposal,
  decision: CARE_HISTORY_LABEL.decision,
  monthly_review: CARE_HISTORY_LABEL.monthly_review,
  plan_changed: CARE_HISTORY_LABEL.plan_changed,
  renewal: CARE_HISTORY_LABEL.renewal,
  lab_feedback: CARE_HISTORY_LABEL.lab_feedback,
  meeting_summary: MEETING_SUMMARY_FIELD_LABEL,
};

// TES-1612 — the same titles for an English patient. The previews reuse
// Din vård's English history labels. From en.json: meeting_info and
// meeting_proposal (vardmoten.kind.*); "Medical notice" is the singular of
// healthCare.notices.title ("Medical notices"). The meeting summary's label
// is new English (a copy proposal for Emelie).
const DOCTOR_FEED_LABEL_EN: typeof DOCTOR_FEED_LABEL = {
  doctor_notice: "Medical notice",
  medical_question_answer: "Medical notice",
  meeting_info: "About your consultation",
  meeting_proposal: "Suggested extra consultation",
  decision: CARE_HISTORY_LABEL_BY_LOCALE.en.decision,
  monthly_review: CARE_HISTORY_LABEL_BY_LOCALE.en.monthly_review,
  plan_changed: CARE_HISTORY_LABEL_BY_LOCALE.en.plan_changed,
  renewal: CARE_HISTORY_LABEL_BY_LOCALE.en.renewal,
  lab_feedback: CARE_HISTORY_LABEL_BY_LOCALE.en.lab_feedback,
  meeting_summary: "Summary after your consultation",
};
export const DOCTOR_FEED_LABEL_BY_LOCALE: Record<
  Locale,
  typeof DOCTOR_FEED_LABEL
> = { sv: DOCTOR_FEED_LABEL, en: DOCTOR_FEED_LABEL_EN };

// The system posts' sentences.
export const DOCTOR_FEED_SYSTEM_TEXT = {
  // TES-1458 §3 / "Förtydligat patientflöde 2026-10-20", verbatim: "EFTER
  // AKTIVERING — godkänd patienttext när aktivering och faktiskt
  // receptutfärdande är verifierade och uthämtning är relevant".
  prescription_ready:
    "Ditt recept är klart. Nu kan du hämta ut ditt läkemedel.",
  // Ö-840 (Emelie 2026-10-22), verbatim: "Godkänd endast som reservtext:
  // 'Din behandling är aktiverad.' Får användas som visningstext när
  // aktivering är bekräftad och inget relevant uthämtningsbesked finns."
  // Shown for her first released version when she continues on her
  // existing prescription (`existing_valid`); a newly issued prescription
  // gets `prescription_ready` instead. A first cycle cannot activate without
  // one of the two choices (TES-1584).
  activation: "Din behandling är aktiverad.",
} as const satisfies Record<"activation" | "prescription_ready", string>;

// TES-1612 — prescription_ready is en.json dinVard.treatment.pickupDue; the
// activation sentence is new English (a copy proposal for Emelie).
export const DOCTOR_FEED_SYSTEM_TEXT_BY_LOCALE: Record<
  Locale,
  Record<"activation" | "prescription_ready", string>
> = {
  sv: DOCTOR_FEED_SYSTEM_TEXT,
  en: {
    prescription_ready:
      "Your prescription is ready. You can now collect your medication.",
    activation: "Your treatment has been activated.",
  },
};

export const doctorFeedSenderSchema = z.discriminatedUnion("kind", [
  // The signing doctor's name, or null if the account is gone.
  z.object({ kind: z.literal("doctor"), name: z.string().nullable() }),
  z.object({ kind: z.literal("system") }),
]);
export type DoctorFeedSender = z.infer<typeof doctorFeedSenderSchema>;

export const doctorFeedItemSchema = z.object({
  // Stable across fetches, unique in the list.
  id: z.string(),
  type: doctorFeedItemTypeSchema,
  displayMode: doctorFeedDisplayModeSchema,
  // DOCTOR_FEED_LABEL; null on a system post, whose `text` is all it says.
  label: z.string().nullable(),
  sender: doctorFeedSenderSchema,
  // ISO timestamp the list is ordered by, oldest first.
  date: z.string(),
  // Where the item opens: the same target Din vård's history gives the same
  // original (note → the notice, treatment → that version of Din behandling,
  // lab_order → the sampling).
  target: careHistoryTargetSchema,
  // The published notice version whose read state this item shares, and
  // that version's number: opening it here or under Din vård
  // (POST …/medical-notices/:id/read) marks the same row. Null when the
  // original has no notice of its own (a version event, a system post).
  noticeId: z.string().nullable(),
  noticeVersion: z.number().int().positive().nullable(),
  // Whether she has opened the original, by the same reads Din vård and Hem
  // use. Null when no entrance records a read of it: a system post, a
  // version event, a result published with its values, and (until an
  // entrance can mark one) a monthly review.
  read: z.boolean().nullable(),
  // full only: the notice itself, exactly as GET …/medical-notices/:id serves
  // it (the doctor's question with her answer, a meeting message's CTA).
  notice: patientMedicalNoticeSchema.optional(),
  // system only: DOCTOR_FEED_SYSTEM_TEXT.
  text: z.string().optional(),
});
export type DoctorFeedItem = z.infer<typeof doctorFeedItemSchema>;

// TES-1665 (work list 2026-11-02, "ONBOARDING MED EN KANAL") — "Kanalhuvudet
// visar faktiskt deltagande vårdroller enligt avsändarreglerna". The roles of
// Ö-1044, kept apart as the decision requires ("Systemet ska hålla isär
// onboarding_guide_doctor_id, assigned_assessment_doctor_id och
// meeting_doctor_id"):
//   onboarding_guide — the fixed onboarding sender, while her guidance is
//                      shown. Not her case doctor: the role "innebär inte att
//                      läkaren är tilldelad patientens medicinska ärende".
//   assessment       — the doctor actually assigned to her case.
//   meeting          — the doctor of her booked meeting.
// One doctor in several roles is one participant with all of them.
export const DOCTOR_FEED_PARTICIPANT_ROLES = [
  "onboarding_guide",
  "assessment",
  "meeting",
] as const;
export const doctorFeedParticipantRoleSchema = z.enum(
  DOCTOR_FEED_PARTICIPANT_ROLES,
);
export type DoctorFeedParticipantRole = z.infer<
  typeof doctorFeedParticipantRoleSchema
>;

export const doctorFeedParticipantSchema = z.object({
  employeeId: z.string(),
  name: z.string(),
  // Her portrait's key, resolved as the guidance's sender's is; null shows
  // the app's neutral silhouette.
  avatarMediaKey: z.string().nullable(),
  roles: z.array(doctorFeedParticipantRoleSchema).min(1),
});
export type DoctorFeedParticipant = z.infer<typeof doctorFeedParticipantSchema>;

// GET patient-api/v1/messages/doctor — oldest first.
export const doctorFeedResponseSchema = z.object({
  items: z.array(doctorFeedItemSchema),
  // TES-1657 — the doctor's onboarding guidance (onboarding-guide.ts), shown
  // before the items: it is what she is guided through before anything is
  // published to her. Only for a build at or after
  // CLIENT_CONTRACT_CHANGES.onboardingChannel, and only while there is a
  // guiding doctor and something to say; absent otherwise.
  onboarding: onboardingGuideSchema.optional(),
  // TES-1665 — who takes part in the channel, for its header: the onboarding
  // guide first (while `onboarding` is sent), then her case doctors in the
  // order their roles began. Same build gate as `onboarding`; absent, or
  // empty, when no doctor takes part yet.
  participants: z.array(doctorFeedParticipantSchema).optional(),
});
export type DoctorFeedResponse = z.infer<typeof doctorFeedResponseSchema>;
